import type { IExternalFormMultiselectItem } from '@components/ExternalFormMultiselect';
import { ExternalFormMultiselect } from '@components/ExternalFormMultiselect';
import { type CatalogRatioShortData } from '@gql/graphql';
import type { Direction } from '@uikit';
import { Tooltip } from '@uikit';
import { useOnRefAttributeChange } from '@utils/dom';

import cn from 'classnames';
import * as R from 'ramda';
import { useCallback, useMemo, useRef } from 'react';
import type { UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import Styles from './controlledExternalFormMultiselect.module.css';

interface IControlledExternalFormMultiselect extends UseControllerProps<any> {
    onSearchFormOpen?: () => void;
    label?: string;
    labelPosition?: 'left' | 'top';
    disabled?: boolean;
    status?: 'alert' | 'warning' | 'success' | undefined;
    disabledIds?: number[];
    className?: string;
    tooltipText?: string;
    countTitleType?: string;
    wrapperClassName?: string;
    onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
    classNameContainer?: string;
    isTooltipHidden?: boolean;
    direction?: Direction;
    classNameTooltip?: string;
}

const adaptRatiosToMultiselectItems = (items: CatalogRatioShortData[]): IExternalFormMultiselectItem[] =>
    R.pipe(
        R.filter(Boolean),
        R.map((item: CatalogRatioShortData) => ({
            id: item.id,
            label: item.code,
            hideRemoveIfDisabled: true,
            disabled: true,
            data: item,
            catalogRatioGroupId: item.catalogRatioGroupId,
        })) as (x: CatalogRatioShortData[]) => IExternalFormMultiselectItem[],
        R.sortBy(R.prop('label')),
    )(items || []);

export const ControlledExternalFormMultiselect: React.FC<IControlledExternalFormMultiselect> = (props) => {
    const {
        control,
        name,
        rules,
        onSearchFormOpen = () => {},
        label,
        labelPosition,
        disabled,
        disabledIds,
        countTitleType,
        onTooltipAttributeChange,
        classNameContainer,
        isTooltipHidden,
        classNameTooltip,
        direction = 'rightCenter',
    } = props;

    const { field, fieldState } = useController({
        name,
        control,
        rules,
    });

    const onChange = useCallback(
        (value) => {
            if (!value) {
                field.onChange([]);

                return;
            }

            const removedItem = field.value.find(
                (item: CatalogRatioShortData) => !value.some((v: IExternalFormMultiselectItem) => v.id === item.id),
            );

            const filteredFieldValue = field.value.filter((item: CatalogRatioShortData) => {
                if (removedItem.catalogRatioGroupId && removedItem.catalogRatioGroupId === item.catalogRatioGroupId) {
                    return false;
                }

                if (removedItem.id === item.id) {
                    return false;
                }

                return true;
            });

            field.onChange(filteredFieldValue);
        },
        [field],
    );

    const errorMessage = useMemo(
        () => (fieldState.invalid && fieldState.error?.message) || '',
        [fieldState.error?.message, fieldState.invalid],
    );

    const tooltipRef = useRef<HTMLDivElement>(null);
    const anchorRef = useRef<HTMLDivElement>(null);

    useOnRefAttributeChange({
        targetRef: tooltipRef,
        onAttributeChange: onTooltipAttributeChange,
    });

    const adaptedSelectedRatiosItems = useMemo(() => adaptRatiosToMultiselectItems(field.value), [field.value]);

    return (
        <div className={cn(classNameContainer, Styles.ControlledExternalFormMultiselect)}>
            <ExternalFormMultiselect
                onChange={onChange}
                selectedItems={adaptedSelectedRatiosItems}
                status={errorMessage ? 'alert' : undefined}
                label={label}
                disabled={disabled}
                labelPosition={labelPosition}
                disabledIds={disabledIds}
                onSearchFormOpen={onSearchFormOpen}
                countTitleType={countTitleType}
            />
            {!isTooltipHidden && errorMessage && (
                <Tooltip
                    size="s"
                    anchorRef={anchorRef}
                    direction={direction}
                    possibleDirections={[direction]}
                    status="alert"
                    className={`${classNameTooltip} tooltip-${name}`}
                    ref={tooltipRef}
                >
                    {errorMessage}
                </Tooltip>
            )}
        </div>
    );
};

ControlledExternalFormMultiselect.displayName = 'ControlledExternalFormMultiselect';
